import { useApi } from "@/hooks/useApi";
import { cn } from "@/lib/utils";
import { CaretSortIcon } from "@radix-ui/react-icons";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@radix-ui/react-popover";
import { AdminUserDTO } from "@shaarp/common";
import { CheckIcon, Loader2, RotateCcw } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Button } from "../ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../ui/command";
import { useToastError } from "../ui/use-toast";

export const UserSelector = ({
  onSelect,
  defaultSelectedUserId,
  filter,
}: {
  onSelect: (user: AdminUserDTO) => void;
  defaultSelectedUserId?: string | null;
  filter?: (user: AdminUserDTO) => boolean;
}) => {
  const { t } = useTranslation();
  const api = useApi();

  const toastError = useToastError();
  const [open, setOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AdminUserDTO | null>(null);
  const [users, setUsers] = useState<AdminUserDTO[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, [defaultSelectedUserId]);

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const { data } = await api.get<AdminUserDTO[]>("/admin/users");

      const filteredUsers = filter ? data.filter(filter) : data;

      if (defaultSelectedUserId) {
        const defaultUser = filteredUsers.find(
          (u) => u.id === defaultSelectedUserId
        );
        if (defaultUser) {
          setSelectedUser(defaultUser);
        }
      }
      setUsers(filteredUsers);
    } catch (error) {
      toastError();
    } finally {
      setIsLoading(false);
    }
  };

  const filterUsers = (value: string, search: string) => {
    const user = users.find((u) => u.id === value);
    if (!user) return 0;

    const searchLower = search.toLowerCase();
    const fullNameMatch = user.fullName.toLowerCase().includes(searchLower);
    const emailMatch = user.email.toLowerCase().includes(searchLower);

    return fullNameMatch || emailMatch ? 1 : 0;
  };

  return (
    <div className="flex gap-2 items-center">
      <div className="min-w-0 flex-1">
        <Popover modal open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              className="justify-between w-full text-sm"
              disabled={isLoading}
            >
              {isLoading ? (
                <Loader2 className="mr-2 w-4 h-4 animate-spin" />
              ) : selectedUser ? (
                <div className="flex items-center">
                  <Avatar className="mr-2 w-4 h-4">
                    <AvatarImage
                      src={selectedUser.imageUrl}
                      alt={selectedUser.fullName || ""}
                    />
                    <AvatarFallback className="text-xs">
                      {selectedUser.firstName?.charAt(0) || "U"}
                    </AvatarFallback>
                  </Avatar>
                  {selectedUser.fullName}
                </div>
              ) : (
                <span className="text-muted-foreground">
                  {t(
                    "admin.team.creation.fields.userSelector.selectUserPlaceholder"
                  )}
                </span>
              )}
              <CaretSortIcon className="ml-auto w-4 h-4 opacity-50 shrink-0" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="z-50 p-0 w-full rounded-md border shadow-xs bg-background"
            side="bottom"
            align="start"
          >
            <Command filter={filterUsers}>
              <CommandInput
                placeholder={t(
                  "admin.team.creation.fields.referral.searchUsers"
                )}
                className="h-9"
              />
              <CommandEmpty>
                {t("admin.team.creation.fields.referral.noUsersFound")}
              </CommandEmpty>
              <CommandList>
                <CommandGroup>
                  {users.map((user) => (
                    <CommandItem
                      key={user.id}
                      value={user.id}
                      onSelect={() => {
                        setSelectedUser(user);
                        setOpen(false);
                        onSelect(user);
                      }}
                    >
                      <Avatar className="mr-2 w-4 h-4">
                        <AvatarImage
                          src={user.imageUrl}
                          alt={user.fullName || ""}
                        />
                        <AvatarFallback className="text-xs">
                          {user.firstName?.charAt(0) || "U"}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">
                          {user.fullName}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {user.email}
                        </span>
                      </div>
                      <CheckIcon
                        className={cn(
                          "ml-auto h-4 w-4",
                          selectedUser?.id === user.id
                            ? "opacity-100"
                            : "opacity-0"
                        )}
                      />
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      <Button
        variant="outline"
        size="icon"
        className="w-9 h-9 shrink-0"
        disabled={isLoading || !selectedUser}
        onClick={() => {
          setSelectedUser(null);
          onSelect(null as any);
        }}
      >
        <RotateCcw className="w-4 h-4" />
      </Button>
    </div>
  );
};
